/**
 * Derived-bounds refresh of INSERT (`Block`) metadata-index records — the
 * derived-index half of a block-table OR named-DIMSTYLE change, run by
 * `commitMutation`.
 *
 * WHY: since the cache-direct INSERT render fix, a `Block` record's
 * `bounds` in `EntityMetadataIndex` is the transformed bbox of the referenced
 * definition's children (`blockExtentsCache.ts`), not the insertion point.
 * That box is DERIVED from `document.blocks[blockName]`, so it goes stale the
 * moment the definition is replaced — a BEDIT session edit / BSAVE / BCLOSE-
 * save (`blockEditReducer.ts::syncBlockEditSpaceToDefinition`), a block
 * redefine, or an attribute sync (`attributeReducerHelpers.ts`) all replace
 * `document.blocks` immutably WITHOUT touching the INSERT entities, so the
 * reducer's `EntityMutation` carries no `updated` entry for them and the
 * plain `added`/`updated` upserts in `commitMutation` never revisit them.
 * Every consumer of the record (cache-direct spatial stubs, fit-zoom, LOD
 * pre-classify, `entityWorldBounds`, rbush candidates) would keep reading
 * the OLD definition's extents.
 *
 * TRIGGER (1) block table: `prevDoc.blocks !== nextDoc.blocks` inside `commitMutation`
 * (the single commit choke point — CLAUDE.md ПРИОРИТЕТ #2.1). Every reducer
 * that changes a definition replaces `document.blocks` with a NEW object
 * (the same invariant `blockExtentsCache.ts` keys its memo on and the same
 * guard `editorStore.ts` already uses for
 * `refreshBlockDefDependencyIndexContainers`), so the identity compare is a
 * by-construction signal that needs no per-reducer declaration. A dedicated
 * `StyleTableTouchKind` was deliberately NOT added: the block table is
 * written by several reducers (block-edit mirror, redefine, attribute sync)
 * and the block-edit mirror fold in `geometryReducer.ts` discards a
 * mutation record that carries no entity-level diff, so a declared touch
 * would have to be threaded through every one of those sites to be reliable
 * — the identity compare covers all of them at once.
 *
 * TRIGGER (2) DIMSTYLE table (Codex PR #784 round 4 P1):
 * `mutations.styleTablesTouched.has("dimensionStyles")`, also inside
 * `commitMutation`. Here the opposite choice is right — the DECLARED signal,
 * not an identity compare: `styleTablesReducer.ts` is the ONE reducer that
 * writes `styleTables.dimensionStyles` on the dispatch contour and it declares
 * the touch on every arm (add / update / delete / rename), and the
 * `header/restore-style-tables-snapshot` inverse recomputes the touch set by
 * VALUE — so undo and redo of a DIMSTYLE edit run through this same path. A
 * block-child `Dimension`'s extents come from its NAMED style's DIMVARs, so
 * editing that style changes the containing definition's footprint while
 * `blocks` stays IDENTICAL (trigger 1 sees nothing, and a style-only mutation
 * declares no entity upserts either — the stale RBush box would survive both
 * the edit and its undo). The refreshed set is NARROWED to
 * {@link dimensionBearingBlockClosure} — definitions that actually contain a
 * `Dimension`, directly or through a nested INSERT / MLEADER BLOCK content —
 * so an ordinary drawing pays one block-table walk and no index scan.
 *
 * PRECISION: the compare is exact only in PRODUCTION. `assembleDrawingDocument`
 * (`drawing-model/src/model.ts`) returns its input by reference only under
 * `import.meta.env.PROD`; in dev/test it runs `drawingDocumentSchema.parse`,
 * which deep-clones the whole document — `blocks` included — so nearly every
 * entity mutation (a model-space `geometry/create-line`, say) yields a new
 * `blocks` object whose per-name definitions are all fresh objects. There the
 * trigger OVER-fires: `changedBlockDefinitionClosure` lists every name and
 * every `Block` record is rebuilt. This is harmless — the rebuilt bounds are
 * computed from structurally identical definitions, so the same values are
 * re-upserted (pinned by the unrelated-mutation isolation test in
 * `__tests__/editorStore.blockRedefinitionMetadata.test.ts`) — and costs an
 * index scan only where the dev/test build already pays a full-document
 * Zod parse per dispatch. The sibling `refreshBlockDefDependencyIndexContainers`
 * guard in `editorStore.ts` keys on the same reference and shares the same
 * dev/test divergence. Production keeps identity, so the scan is paid only on
 * a real block-table change.
 *
 * WHAT: (1) the set of definition names whose object changed
 * (`prev[name] !== next[name]`, added and removed names included), closed
 * transitively over the definitions that CONTAIN a reference to a changed
 * name — a nested INSERT (`type === "Block"`) OR a `Multileader` child whose
 * `contentType === 1` BLOCK content names it (Codex PR #784 round 3 P1: that
 * content's geometry is UNIONed into the containing definition's footprint by
 * `cad-core`'s `multileaderBlockContentLocalBounds`, so redefining the content
 * block changes the container's INSERT bounds exactly as a nested INSERT does;
 * without this arm, redefining B refreshed direct INSERTs of B but left INSERTs
 * of an A that only MLEADER-references B holding stale extents). A visited set
 * makes a cyclic definition graph terminate; (2) for every `Block`
 * record in the index whose entity references a name in that closure, a
 * fresh record built by the caller's `buildMetadata` (the same `toMetadata`
 * the `added`/`updated` upserts use, against the COMMITTED `next.blocks`).
 *
 * `geomRev` — the refreshed record takes `prevRev + 1`, in parity with what
 * the `updated` path in `commitMutation` assigns for a `"geometry"`-class
 * change. `classifyEntityChange` would call the INSERT entity itself
 * unchanged, but an INSERT's rendered geometry IS its definition's children
 * under its transform: the definition changing is a geometry change of the
 * INSERT's derived render output and bounds, and any `(id, geomRev)`-keyed
 * cache (`batchedLayer.ts` tessellation cache) must not reuse a buffer built
 * from the old definition. NOTE (pre-existing gap, out of scope here):
 * `EntityMetadataIndex.toInternal` / `toPublic` do not persist `geomRev`, so
 * a stored record reads it back as `undefined` (→ 0) and the bump currently
 * always lands on 1 — the same effective behaviour as the `updated` path.
 * The `prevRev + 1` contract becomes fully effective once the index persists
 * `geomRev`; the code is kept so that change needs no edit here.
 *
 * Pure: the index, the entity resolver and the metadata builder are injected
 * so the module stays free of the store singletons and is unit-testable on a
 * bare `EntityMetadataIndex`.
 */

import type {
  BlockDefinition,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import type { BlockTable } from "./blockExtentsCache";
import type {
  EntityMetadata,
  EntityMetadataIndex,
} from "./entityMetadataIndex";

const EMPTY_BLOCKS: BlockTable = {};

const hasOwn = (table: BlockTable, name: string): boolean =>
  Object.prototype.hasOwnProperty.call(table, name);

/**
 * The definition name ONE block-definition child pulls another definition's
 * geometry in through, or `undefined` for a child that references none.
 *
 * Mirrors `@dxf-ai-editor/cad-core`'s `blockDefinitionWalk.ts
 * ::childEntityBounds` one-for-one — the two kinds whose bounds arm resolves
 * a SECOND definition:
 *   - `Block` — a nested INSERT, placed by the INSERT affine + MINSERT grid;
 *   - `Multileader` with `contentType === 1` — its BLOCK content, placed at
 *     `textLocation` by the synthetic content INSERT and UNIONed into the
 *     container's footprint (Codex PR #784 round 3 P1).
 *
 * An EMPTY name is treated as no reference (cad-core's own lookup resolves it
 * to "no definition"), so it never seeds a bogus containment edge — a `""`
 * key would otherwise collect every content-less MLEADER's container and
 * refresh them on an unrelated redefinition.
 */
function childReferencedBlockName(
  child: BlockDefinition["entities"][number],
): string | undefined {
  if (child.type === "Block") return child.blockName || undefined;
  if (child.type === "Multileader" && child.contentType === 1) {
    return child.blockName || undefined;
  }
  return undefined;
}

/**
 * Names of the definitions that differ between the two block tables (object
 * identity per name; added and removed names count), closed transitively
 * over every definition in `nextBlocks` that REFERENCES a name already in the
 * set — its transformed extents change too. Returns an empty set when the
 * tables are the same object. Terminates on cyclic definition graphs
 * (visited-set BFS).
 *
 * A "reference" is exactly what `@dxf-ai-editor/cad-core`'s definition-bounds
 * walk (`blockDefinitionWalk.ts::childEntityBounds`) pulls another
 * definition's geometry in through — {@link childReferencedBlockName}:
 *   - a nested INSERT child (`type === "Block"` → `child.blockName`);
 *   - a `Multileader` child with BLOCK content (`contentType === 1` →
 *     `child.blockName`, placed by `blockInsertInstance.ts
 *     ::makeMleaderContentInsert` and UNIONed into the container's bounds by
 *     `multileaderBlockContentLocalBounds`) — Codex PR #784 round 3 P1.
 * Keeping the two in lockstep is the contract: any future kind whose bounds
 * arm resolves ANOTHER definition must be added here too, or INSERTs of the
 * container go stale on that definition's redefinition.
 */
export function changedBlockDefinitionClosure(
  prevBlocks: BlockTable | undefined,
  nextBlocks: BlockTable | undefined,
): Set<string> {
  const closure = new Set<string>();
  if (prevBlocks === nextBlocks) return closure;
  const prev = prevBlocks ?? EMPTY_BLOCKS;
  const next = nextBlocks ?? EMPTY_BLOCKS;
  // Own-property checks (not `in`): a definition named `constructor` /
  // `__proto__` / `hasOwnProperty` on a `{}`-literal table must not be
  // mistaken for present via `Object.prototype`.
  for (const name of Object.keys(prev)) {
    if (!hasOwn(next, name) || prev[name] !== next[name]) closure.add(name);
  }
  for (const name of Object.keys(next)) {
    if (!hasOwn(prev, name)) closure.add(name);
  }
  return closeOverContainers(closure, next);
}

/**
 * Names of the definitions whose footprint contains a `Dimension` — directly
 * as a child, or transitively through a nested INSERT / MLEADER BLOCK content
 * ({@link closeOverContainers}). Empty for `undefined` blocks.
 *
 * This is the NARROWING of the DIMSTYLE-touch refresh (Codex PR #784 round 4
 * P1): editing a named DIMSTYLE changes the derived bounds of exactly those
 * INSERTs whose definition draws a Dimension with the edited DIMVARs, and of
 * nothing else. An ordinary drawing (no Dimension inside any block) yields an
 * empty set, so `blockInsertMetadataRefreshUpserts` returns immediately and
 * NO index scan is paid. The seed is deliberately NOT narrowed further to the
 * EDITED style's name: a block-child `Dimension` resolves its DIMVARs by
 * `styleName` against the whole table, and a rename / delete / the
 * `"Standard"` fallback all move a child from one row to another — a
 * name-level filter would miss exactly those, while the walk that would prove
 * it costs the same as the walk done here.
 */
export function dimensionBearingBlockClosure(
  blocks: BlockTable | undefined,
): Set<string> {
  const seed = new Set<string>();
  if (blocks === undefined) return seed;
  for (const name of Object.keys(blocks)) {
    const def: BlockDefinition | undefined = blocks[name];
    if (!def) continue;
    for (const child of def.entities) {
      if (child.type === "Dimension") {
        seed.add(name);
        break;
      }
    }
  }
  return closeOverContainers(seed, blocks);
}

/**
 * Grow `seed` (IN PLACE, and return it) over the reverse-containment graph of
 * `blocks`: every definition that pulls a seeded definition's geometry into
 * its OWN footprint — a nested INSERT or MLEADER BLOCK content, exactly
 * {@link childReferencedBlockName} — is affected too, transitively. A visited
 * set (the `seed` itself) makes a cyclic definition graph terminate. No index
 * is built for an empty seed.
 */
function closeOverContainers(seed: Set<string>, blocks: BlockTable): Set<string> {
  if (seed.size === 0) return seed;
  // Reverse containment: referenced block name → definitions that pull its
  // geometry into their own footprint (nested INSERT or MLEADER block content).
  const containers = new Map<string, string[]>();
  for (const defName of Object.keys(blocks)) {
    const def: BlockDefinition | undefined = blocks[defName];
    if (!def) continue;
    for (const child of def.entities) {
      const referenced = childReferencedBlockName(child);
      if (referenced === undefined) continue;
      const list = containers.get(referenced);
      if (list) list.push(defName);
      else containers.set(referenced, [defName]);
    }
  }
  const queue = [...seed];
  while (queue.length > 0) {
    const name = queue.pop()!;
    const parents = containers.get(name);
    if (!parents) continue;
    for (const parent of parents) {
      if (seed.has(parent)) continue;
      seed.add(parent);
      queue.push(parent);
    }
  }
  return seed;
}

/**
 * The refreshed `EntityMetadata` records for every `Block` (INSERT) record in
 * `index` whose entity references a definition in `changedBlocks`.
 *
 * - `resolveEntity` — the caller's id → typed entity read path; an id that
 *   does not resolve to a `Block` entity is skipped (its record is left as
 *   is — nothing to rebuild it from).
 * - `excludeIds` — ids the SAME commit already upserts or deletes from its
 *   declared mutations (they were built against the committed table
 *   already, or are gone).
 * - `buildMetadata(entity, spaceId)` — the caller's `toMetadata` bound to
 *   the committed block table; `spaceId` is the existing record's.
 *
 * Empty when `changedBlocks` is empty (no index scan is paid).
 */
export function blockInsertMetadataRefreshUpserts(args: {
  index: EntityMetadataIndex;
  changedBlocks: ReadonlySet<string>;
  excludeIds: ReadonlySet<string>;
  resolveEntity: (id: string) => DrawingEntity | undefined;
  buildMetadata: (entity: DrawingEntity, spaceId: string) => EntityMetadata;
}): EntityMetadata[] {
  const { index, changedBlocks, excludeIds, resolveEntity, buildMetadata } =
    args;
  const upserts: EntityMetadata[] = [];
  if (changedBlocks.size === 0) return upserts;
  for (const record of index.filterByType("Block")) {
    if (excludeIds.has(record.id)) continue;
    const entity = resolveEntity(record.id);
    if (!entity || entity.type !== "Block") continue;
    if (!changedBlocks.has(entity.blockName)) continue;
    const prevRev = record.geomRev ?? 0;
    upserts.push({
      ...buildMetadata(entity, record.spaceId),
      geomRev: prevRev + 1,
    });
  }
  return upserts;
}
